import { useCallback, useEffect, useRef, useState } from 'react'

import { SegmentResponse } from '@/api/type'
import { SILENCE_TEXT } from '@/lib/constants'
import { PLAYBACK_MODES, PlaybackMode } from '@/types/playback'

type UseAutoPauseProps = {
  enabled: boolean
  multiplier: number
  isPlaying: boolean
  transcription: { segments: SegmentResponse[] } | null
  playbackMode: PlaybackMode
  setCurrentSegmentIndex: (index: number | null) => void
  pause: () => void
  play: () => void
}

type UseAutoPauseReturn = {
  isPausedByAuto: boolean
  autoPauseProgress: number
  autoPauseTotal: number
  cancelAutoPause: () => void
  handleAutoPauseCheck: (previousTime: number, audioCurrentTime: number) => boolean
}

/**
 * 自動停止機能を管理するカスタムフック
 * セグメント終了時に自動的に一時停止し、指定時間後に再生を再開
 */
export const useAutoPause = ({
  enabled,
  multiplier,
  isPlaying,
  transcription,
  playbackMode,
  setCurrentSegmentIndex,
  pause,
  play,
}: UseAutoPauseProps): UseAutoPauseReturn => {
  const [isPausedByAuto, setIsPausedByAuto] = useState(false)
  const [autoPauseProgress, setAutoPauseProgress] = useState(0)
  const [autoPauseTotal, setAutoPauseTotal] = useState(0)
  const [lastPausedSegment, setLastPausedSegment] = useState(-1)

  const autoPauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoPauseInterval = useRef<ReturnType<typeof setInterval> | null>(null)

  // 自動停止をキャンセル
  const cancelAutoPause = useCallback(() => {
    if (autoPauseTimer.current) {
      clearTimeout(autoPauseTimer.current)
      autoPauseTimer.current = null
    }
    if (autoPauseInterval.current) {
      clearInterval(autoPauseInterval.current)
      autoPauseInterval.current = null
    }
    setIsPausedByAuto(false)
    setAutoPauseProgress(0)
  }, [])

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (autoPauseTimer.current) {
        clearTimeout(autoPauseTimer.current)
      }
      if (autoPauseInterval.current) {
        clearInterval(autoPauseInterval.current)
      }
    }
  }, [])

  // 自動停止のチェックと実行
  const handleAutoPauseCheck = useCallback(
    (previousTime: number, audioCurrentTime: number): boolean => {
      if (!enabled || isPausedByAuto || !isPlaying || !transcription) {
        return false
      }

      // 前回の時間と現在の時間の間にセグメント終了時刻が存在するかチェック
      const segmentToStop = transcription.segments.find((segment, index) => {
        // まだ停止していないセグメント、かつ無音セグメントではない
        return (
          lastPausedSegment !== index &&
          segment.text !== SILENCE_TEXT &&
          segment.end > previousTime &&
          segment.end <= audioCurrentTime
        )
      })

      if (!segmentToStop) {
        return false
      }

      const segmentIndex = transcription.segments.indexOf(segmentToStop)
      console.log('Auto pause triggered for segment:', segmentIndex, 'end time:', segmentToStop.end)

      const segmentDuration = segmentToStop.end - segmentToStop.start
      const pauseDuration = Math.max(segmentDuration * multiplier * 1000, 100) // 最低100ms

      setLastPausedSegment(segmentIndex)
      setCurrentSegmentIndex(segmentIndex) // 自動停止中は停止したセグメントを選択
      setIsPausedByAuto(true)
      setAutoPauseTotal(pauseDuration)
      setAutoPauseProgress(0)
      pause()

      // 進行状況を更新
      const startTime = Date.now()
      autoPauseInterval.current = setInterval(() => {
        const elapsed = Date.now() - startTime
        setAutoPauseProgress(Math.min(elapsed, pauseDuration))

        if (elapsed >= pauseDuration) {
          if (autoPauseInterval.current) {
            clearInterval(autoPauseInterval.current)
            autoPauseInterval.current = null
          }
        }
      }, 50)

      // 指定時間後に再生再開
      autoPauseTimer.current = setTimeout(() => {
        console.log('Auto pause finished, resuming playback')
        setIsPausedByAuto(false)
        setAutoPauseProgress(0)
        if (autoPauseInterval.current) {
          clearInterval(autoPauseInterval.current)
          autoPauseInterval.current = null
        }
        play()

        // 再生再開後すぐに次のセグメントを選択
        setTimeout(() => {
          if (transcription) {
            // 停止したセグメントの次のセグメント（無音を除く）を探す
            let nextSegmentIndex = -1
            for (let i = segmentIndex + 1; i < transcription.segments.length; i++) {
              if (transcription.segments[i].text !== SILENCE_TEXT) {
                nextSegmentIndex = i
                break
              }
            }

            if (playbackMode === PLAYBACK_MODES.ALL) {
              setCurrentSegmentIndex(nextSegmentIndex >= 0 ? nextSegmentIndex : null)
            }
          }
        }, 50)
      }, pauseDuration)

      return true
    },
    [
      enabled,
      isPausedByAuto,
      isPlaying,
      transcription,
      lastPausedSegment,
      multiplier,
      pause,
      play,
      setCurrentSegmentIndex,
      playbackMode,
    ],
  )

  return {
    isPausedByAuto,
    autoPauseProgress,
    autoPauseTotal,
    cancelAutoPause,
    handleAutoPauseCheck,
  }
}
